import React, { useState, useRef, useEffect } from "react";
import { BlobServiceClient } from "@azure/storage-blob";
import { useUserContext } from "../../context/userContext";
import Button from "../Button";
import fetchWithRefresh from "../../utils/fetchWithRefresh";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { LogErrors } from "../../utils/LogErrors";

export default function AzureProfilePictureUpload({ activeAccount, participant, temporaryImage, temporaryImageName, uploadExternal = false }) {
    const profilePictureRef = useRef(null);
    const { user, logout, updateTokens } = useUserContext();
    const [file, setFile] = useState(null);
    const [fileName, setFileName] = useState(null);
    const [previewImage, setPreviewImage] = useState(null);
    const [uploadingFile, setUploadingFile] = useState(false);
    const [fileUploaded, setFileUploaded] = useState(false);
    const [CDNFileURL, setCDNFileURL] = useState(null);

    const handleFileInputClick = () => {
        if (profilePictureRef.current) {
            profilePictureRef.current.click();
        }
    };

    const fileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => { 
                resolve(reader.result);
                setPreviewImage(reader.result); // Set the preview URL;
                setFileName(file.name);
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    };

    const handleFileChange = async (event) => {
        if (event.target.files[0]) {
            if(uploadExternal){
                const base64 = await fileToBase64(event.target.files[0]); // Convert the file to base64
                setFile(base64);
            } else {
                setFile(event.target.files[0]);
                const reader = new FileReader();
                reader.onloadend = () => {
                    setPreviewImage(reader.result); // Set the preview URL
                };
                reader.readAsDataURL(event.target.files[0]); // This will trigger reader.onloadend
            }
        }
    };

    const handleFileUpload = async () => {
        if (!file) return;

        setUploadingFile(true);
        
        const account = import.meta.env.VITE_AZURE_STORAGE_ACCOUNT;
        const sasKey = import.meta.env.VITE_AZURE_PROFILE_PICTURE_STORAGE_SAS_TOKEN;
        const container = import.meta.env.VITE_AZURE_PROFILE_PICTURE_STORAGE;

        try {
            const blobServiceClient = new BlobServiceClient(`https://${account}.blob.core.windows.net?sv=${sasKey}`);
            const containerClient = blobServiceClient.getContainerClient(container);
            const lastDotIndex = file.name.lastIndexOf('.');
            const fileExtension = lastDotIndex > 0 ? file.name.substring(lastDotIndex + 1) : ''; 

            let blobName = "";

            if (activeAccount?.UserLoginId !== undefined) {
                blobName = `${activeAccount?.UserLoginId}-profile-picture.${fileExtension}`;
            } else if (activeAccount?.ParticipantId !== undefined) {
                blobName = `${activeAccount?.ParticipantId}-profile-picture.${fileExtension}`;
            } else {
                LogErrors(user, logout, updateTokens, "Error with filename");
            }

            const blockBlobClient = containerClient.getBlockBlobClient(blobName);
            const arrayBuffer = await file.arrayBuffer();
            const uploadBlobResponse = await blockBlobClient.upload(arrayBuffer, arrayBuffer.byteLength);

            if (uploadBlobResponse._response.status === 201) {
                const uploadedBlobUrl = `https://${account}.blob.core.windows.net/${container}/${blobName}`;
                if (participant) {
                    setFileUploaded(true);
                    setCDNFileURL(uploadedBlobUrl);
                } else {
                    handleUserProfileUpdate(uploadedBlobUrl);
                }
            } else {
                LogErrors(user, logout, updateTokens, "Upload failed. Status code: " + uploadBlobResponse._response.status);
                alert("Upload failed. Please try again.");
            }
        } catch (error) {
            LogErrors(user, logout, updateTokens, "Error uploading file: " + error);
            setUploadingFile(false);
        }
    };

    const handleUserProfileUpdate = async (cdnPictureURL) => {
        await Sentry.startSpan(
            {
                name: "Update User Profile Picture",
                op: "http.request",
            },
            async (span) => {
                try {
                    const req = await fetchWithRefresh(import.meta.env.VITE_API_URL + `Users/UpdateAccountProfilePicture`, {
                        method: "PUT",
                        body: JSON.stringify({
                            userLoginId: user?.id,
                            profilePicture: cdnPictureURL,
                        }),
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${user.token}`,
                        },
                    }, logout, updateTokens, (message) => { toast.info(message); });

                    // Set HTTP response status in the span
                    span.setAttribute("http.status_code", req.status);

                    if (req.status === 200) {
                        setPreviewImage(false);
                        setFileUploaded(true);
                        setCDNFileURL(cdnPictureURL);
                    } else {
                        span.setStatus("error"); // Mark span as error
                        LogErrors(user, logout, updateTokens, "User Profile Picture failed to update");
                    }
                } catch (error) {
                    span.setStatus("error");
                    Sentry.captureException(error);
                    LogErrors(user, logout, updateTokens, error);
                } finally {
                    setUploadingFile(false);
                    span.end(); // Explicitly end the span
                }
            }
        );
    };

    return (
        <div className="flex flex-row gap-6 items-center">
            {previewImage ? (
                <img className='rounded-full border-[2px] border-[#EEEEF1] object-cover h-[62px]' src={previewImage} width="62" height="62" />
            ) : temporaryImage ? (
                <img className='rounded-full border-[2px] border-[#EEEEF1] object-cover h-[62px]' src={temporaryImage} width="62" height="62" />
            ) : activeAccount?.ProfilePicture ? (
                <img className='rounded-full border-[2px] border-[#EEEEF1] object-cover h-[62px]' src={activeAccount?.ProfilePicture + `?t=${Date.now()}`} width="62" height="62" />
            ) : (
                <img src="/images/default-avatar.svg" width="62" height="62" />
            )}

            <div className="">
                <h4 className="mb-1">Profile Picture</h4>
                <input
                    type="file"
                    ref={profilePictureRef}
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                />

                {!uploadExternal ? (
                    <>
                        {fileUploaded || (activeAccount?.ProfilePicture && !previewImage) ? (
                            <>
                                {fileUploaded && (<p className="text-sportsGreen">Profile picture successfully updated.</p>)}
                                <input type="hidden" value={CDNFileURL || activeAccount?.ProfilePicture} name="uploadedProfilePicture"/>
                                {activeAccount?.ProfilePicture && !previewImage && (
                                    <Button variant="tertiary" status="nobg" onClick={handleFileInputClick}>Change</Button>
                                )}
                            </>
                        ) : uploadingFile ? (
                            <p>Profile picture saving...</p>
                        ) : previewImage ? (
                            <Button variant="tertiary" status="nobg" onClick={handleFileUpload}>Save</Button>
                        ) : (
                            <Button variant="tertiary" status="nobg" onClick={handleFileInputClick}>+ Add</Button>
                        )}
                    </>
                ) : (
                    <>
                        <input type="hidden" value={CDNFileURL || temporaryImage || previewImage || activeAccount?.ProfilePicture} name="uploadedProfilePicture"/>
                        <Button variant="tertiary" status="nobg" onClick={handleFileInputClick}>
                            {(!temporaryImage && !activeAccount?.ProfilePicture && !previewImage) ? "+ Add" : "Change"}
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
}